import React, { useEffect, useRef, useCallback } from 'react';
import './LazyLoadBackgroundImage.scss';

const LazyLoadBackgroundImage = ({ className = '', src, style = {}, onClick, children }) => {
  const containerRef = useRef(null);
  const lazyloadThrottleTimeout = useRef(null);
  const imageObserver = useRef(null);

  // Handle scrolling fallback for browsers without Intersection Observer support
  const handleScroll = useCallback(() => {
    if (lazyloadThrottleTimeout.current) {
      clearTimeout(lazyloadThrottleTimeout.current);
    }
    lazyloadThrottleTimeout.current = setTimeout(() => {
      const scrollTop = window.pageYOffset;
      const container = containerRef.current;
      if (container && container.offsetTop < window.innerHeight + scrollTop) {
        container.style.backgroundImage = `url(${container.dataset.src})`;
        container.classList.remove('lazy');
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    }, 100); // Increased delay to ensure proper loading
  }, []);

  // Handle lazy loading with Intersection Observer
  const handleIntersectionObserver = useCallback(() => {
    imageObserver.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const container = entry.target;
            container.style.backgroundImage = `url(${container.dataset.src})`;
            container.classList.remove('lazy');
            imageObserver.current.unobserve(container);
          }
        });
      },
      {
        rootMargin: '100px 0px', // Added margin to trigger early load
      }
    );
    if (containerRef.current) {
      imageObserver.current.observe(containerRef.current);
    }
  }, []);

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      handleIntersectionObserver();
    } else {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationChange', handleScroll);
      handleScroll(); // Initial check
    }

    return () => {
      if (imageObserver.current && containerRef.current) {
        imageObserver.current.unobserve(containerRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('orientationChange', handleScroll);
    };
  }, [handleScroll, handleIntersectionObserver]);

  return (
    <div
      ref={containerRef}
      className={`lazy ${className}`}
      data-src={src}
      style={style}
      onClick={onClick || (() => {})}
    >
      {children}
    </div>
  );
};

export default LazyLoadBackgroundImage;
